import type { ThreadMessage } from './chatTypes';
import type { ComposerAttachment } from './streamUpload';

/**
 * CORE-1791 (возврат QA 28.10) — одна попытка отправить сообщение и её ключ повтора.
 *
 * Отказ отправки возвращает текст в поле (CORE-1350), а повтор этого текста
 * раньше уходил новой отправкой. Если задержанная первая всё-таки дошла (сеть
 * вернулась, сокет дослал кадр), сообщений становилось два. Теперь у каждой
 * отправки есть `clientMessageId`: повтор вернувшегося текста идёт с тем же
 * ключом, и сервер отдаёт уже созданное сообщение вместо второй копии
 * (CORE-1703). А дошедшее сообщение приносит ключ с собой — по нему композер
 * узнаёт свою попытку и убирает подсказку «не отправлено» и текст из поля.
 *
 * CORE-1791 (возврат QA 31.10) — попытка — это не только текст. Отказ
 * возвращает в поле и загруженные файлы, и цитату, и ключ принадлежит всему
 * этому вместе: по старому ключу сервер отдал бы уже созданное сообщение, и
 * файл или цитата, которых в нём нет, пропали бы молча.
 */
export interface ChatSendAttempt {
  clientMessageId: string;
  text: string;
  /** Файлы этой отправки (`attachmentSignature`). Нет поля — без файлов. */
  attachments?: string[];
  /** Сообщение, на которое отвечали. Нет поля — без цитаты. */
  replyTo?: string;
  /**
   * CORE-1791 (возврат QA 04.11) — отправка ушла, исхода ещё нет. Поле
   * очищено сразу, и без этой записи перезагрузка до ответа хаба теряла текст
   * насовсем: в черновике пусто, на сервере ничего.
   */
  inFlight?: true;
}

/** Что именно уходит: попытка без ключа. */
export type SendContent = Omit<ChatSendAttempt, 'clientMessageId'>;

/** CORE-1682 — ключ идемпотентности отправки (до 64 символов у сервера). */
export function newClientMessageId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Чем загруженный файл отличается от другого: id на сервере (файлы кругов и
 * обсуждений), иначе ссылка на загруженное (личные сообщения). Один и тот же
 * файл, выбранный дважды, загружается дважды и получает разные — это две
 * разные отправки.
 */
export function attachmentSignature(a: ComposerAttachment): string {
  return String(a.id ?? a.asset_url ?? a.image_url ?? a.file_name ?? a.title ?? '');
}

function sameSend(a: SendContent, b: SendContent): boolean {
  const fa = a.attachments ?? [];
  const fb = b.attachments ?? [];
  return a.text === b.text
    && (a.replyTo ?? '') === (b.replyTo ?? '')
    && fa.length === fb.length
    && fa.every((f, i) => f === fb[i]);
}

/**
 * Ключ для отправки `next`: то же, что вернулось в поле после неудачи (текст,
 * файлы, цитата), — та же попытка и тот же ключ; что-то другое — новая
 * попытка. Пустая отправка попыткой не считается.
 */
export function retryKeyFor(
  prior: ChatSendAttempt | null,
  next: SendContent,
  fresh: () => string = newClientMessageId,
): string {
  const empty = !next.text && !next.attachments?.length;
  return prior && !empty && sameSend(prior, next) ? prior.clientMessageId : fresh();
}

/** Карточка в поле — файл этой попытки. */
export function isAttemptFile(attempt: ChatSendAttempt, a: ComposerAttachment | undefined): boolean {
  return !!a && !!attempt.attachments?.includes(attachmentSignature(a));
}

/**
 * Файлы неотправленного сообщения не пережили перезагрузку (или переход в
 * другой диалог): байты живут только в открытой вкладке, а в черновике —
 * только текст. Говорим об этом до повтора, а не после.
 */
export const FILES_NOT_KEPT = 'Файлы неотправленного сообщения не сохранились — прикрепите их снова.';

/**
 * CORE-1791 (возврат QA 04.11) — страницу перезагрузили, пока отправка ждала
 * ответа: дошла она или нет, неизвестно. Текст вернулся в поле; если сообщение
 * всё же дошло, оно приедет с историей и уберёт текст само.
 */
export const SEND_NOT_CONFIRMED = 'Сообщение могло не отправиться. Текст сохранён: если оно не появится, отправьте ещё раз.';

/**
 * Отправка, начатая до перезагрузки (или в другой вкладке): у неё нет исхода
 * и ждать его на этой странице некому. `startedHere` — ключи отправок этой
 * страницы, их исход ещё придёт.
 */
export function orphanedInFlight(attempt: ChatSendAttempt | null, startedHere: ReadonlySet<string>): boolean {
  return !!attempt?.inFlight && !startedHere.has(attempt.clientMessageId);
}

/** Попытка без пометки «в пути» — то, что хранится после исхода. */
export function settled(attempt: ChatSendAttempt): ChatSendAttempt {
  const { inFlight: _inFlight, ...rest } = attempt;
  return rest;
}

/**
 * Диалог снова открыт, а у него есть неотправленное сообщение. `returnedKey` —
 * ключ отправки, чьи причина отказа, файлы и цитата ждут возврата (отказ
 * пришёл, когда диалог не был открыт). Иначе, если у попытки были файлы, а
 * взять их неоткуда, — сказать об этом (один раз на попытку: `noticed`).
 */
export function reopenStep(
  attempt: ChatSendAttempt | null,
  returnedKey: string | undefined,
  noticed: ReadonlySet<string>,
): 'restore' | 'notice' | null {
  if (!attempt) return null;
  if (returnedKey && returnedKey === attempt.clientMessageId) return 'restore';
  if (!attempt.attachments?.length || noticed.has(attempt.clientMessageId)) return null;
  return 'notice';
}

/** Задержанная отправка дошла: среди своих сообщений есть сообщение с её ключом. */
export function attemptDelivered(messages: readonly ThreadMessage[], attempt: ChatSendAttempt | null): boolean {
  return !!attempt && messages.some((m) => m.isMine && m.clientMessageId === attempt.clientMessageId);
}
